import mitt from "mitt";
import { frontdeskDiagnosticLogger } from "./diagnostic-logger";
import {
  FRONTDESK_LANGUAGE_CATALOG,
  type FrontDeskLanguageCode,
} from "./i18n/language-catalog";
import { reportOperationalError } from "./operational-error-telemetry";
import { importedAudioFiles } from "./audio-assets";

const AUDIO_KEYS = [
  "checkIn-appointmentSelection",
  "checkIn-cardCheck",
  "checkIn-cardCheckin",
  "checkIn-checkingIn",
  "checkIn-diffReview",
  "checkIn-done",
  "checkIn-eebConsent",
  "checkIn-eebPrompt",
  "checkIn-eebRequest",
  "checkIn-insuranceSelection",
  "checkIn-insurerContact",
  "checkIn-insurerPreFilled",
  "checkIn-insurerSearchSelection",
  "checkIn-noAppointmentFoundBookable",
  "checkIn-noAppointmentFoundUnavailable",
  "checkIn-patientLookup",
  "checkIn-patientInfoLoading",
  "checkIn-patientInfoResult",
  "checkIn-patientInfoReview",
  "checkIn-patientRegisterBasics",
  "checkIn-patientRegisterDetails",
  "checkIn-patientRegisterInsurer",
  "checkIn-patientSelection",
  "checkIn-quarterlyCoverageCheck",
  "checkIn-registerFromCard",
  "checkIn-selfPay",
  "prescription-cardCheck",
  "prescription-doneError",
  "prescription-doneNoPrescriptions",
  "prescription-doneSuccess",
  "prescription-medicationSelection",
  "prescription-noPatientFound",
  "prescription-requestConfirmationError",
  "prescription-requestConfirmationSuccess",
  "prescription-requestReview",
  "scheduler-appointmentType",
  "scheduler-confirmation",
  "scheduler-done",
  "scheduler-error",
  "scheduler-patientIdentification",
  "scheduler-slotSelection",
] as const;

/**
 * Available spoken-guidance keys for self-service flow steps.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const key: AudioKey = "checkIn-cardCheck";
 * ```
 */
export type AudioKey = (typeof AUDIO_KEYS)[number];

/**
 * Observable playback lifecycle for the active spoken-guidance key.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const status: AudioPlaybackStatus = "completed";
 * ```
 */
export type AudioPlaybackStatus =
  | "idle"
  | "playing"
  | "completed"
  | "blocked"
  | "error"
  | "unavailable";

/**
 * Reader-safe state exposed to replay controls.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const snapshot: AudioPlaybackSnapshot = {
 *   activeKey: null,
 *   locale: "de",
 *   replayAvailable: false,
 *   status: "idle",
 * };
 * ```
 */
export interface AudioPlaybackSnapshot {
  readonly activeKey: AudioKey | null;
  readonly locale: string;
  readonly replayAvailable: boolean;
  readonly status: AudioPlaybackStatus;
}

interface GuidanceAudio {
  volume: number;
  currentTime: number;
  play(): Promise<void>;
  pause(): void;
  addEventListener(
    type: "ended" | "error",
    listener: (event: Event) => void,
  ): void;
}

interface AudioPlaybackControllerOptions {
  readonly assets: Readonly<
    Partial<Record<string, Partial<Record<AudioKey, string>>>>
  >;
  readonly createAudio: (source: string) => GuidanceAudio;
  readonly initialLocale?: string;
  readonly onCompleted?: (key: AudioKey) => void;
}

interface AudioPlaybackController {
  readonly getSnapshot: () => AudioPlaybackSnapshot;
  readonly subscribe: (listener: () => void) => () => void;
  readonly register: (key: AudioKey, autoPlay: boolean) => number;
  readonly unregister: (registration: number) => void;
  readonly play: (registration: number) => void;
  readonly stop: (registration: number) => void;
  readonly replay: () => void;
  readonly clear: () => void;
  readonly setLocale: (locale: string) => void;
  readonly setVolume: (volume: number) => void;
}

const knownAudioKeys = new Set<string>(AUDIO_KEYS);
const knownLanguages = new Set<string>(
  FRONTDESK_LANGUAGE_CATALOG.map(({ code }) => code),
);

function isAudioKey(value: string): value is AudioKey {
  return knownAudioKeys.has(value);
}

function isSupportedLanguage(value: string): value is FrontDeskLanguageCode {
  return knownLanguages.has(value);
}

function normalizeLocale(locale: string): string {
  return locale.split("-")[0] ?? "";
}

/**
 * Creates an isolated spoken-guidance controller with injectable audio assets.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const controller = createAudioPlaybackController({
 *   assets: {},
 *   createAudio: (source) => new Audio(source),
 * });
 * controller.setLocale("en");
 * ```
 */
export function createAudioPlaybackController(
  options: AudioPlaybackControllerOptions,
): AudioPlaybackController {
  let activeKey: AudioKey | null = null;
  let activeRegistration: number | null = null;
  let registrationSequence = 0;
  let elementSequence = 0;
  let audio: GuidanceAudio | null = null;
  let locale = normalizeLocale(options.initialLocale ?? "de");
  let volume = 0.5;
  let status: AudioPlaybackStatus = "idle";
  const listeners = new Set<() => void>();
  let snapshot: AudioPlaybackSnapshot = {
    activeKey,
    locale,
    replayAvailable: false,
    status,
  };

  const sourceForActiveKey = (): string | undefined => {
    if (activeKey === null) return undefined;
    return options.assets[locale]?.[activeKey];
  };

  const publish = () => {
    snapshot = {
      activeKey,
      locale,
      replayAvailable: sourceForActiveKey() !== undefined,
      status,
    };
    for (const listener of listeners) listener();
  };

  const stopElement = () => {
    elementSequence += 1;
    if (audio !== null) {
      audio.pause();
      audio.currentTime = 0;
      audio = null;
    }
  };

  const playActive = () => {
    stopElement();
    if (activeKey === null) {
      status = "idle";
      publish();
      return;
    }

    const key = activeKey;
    const source = sourceForActiveKey();
    if (source === undefined) {
      status = "unavailable";
      publish();
      frontdeskDiagnosticLogger.warn("Audio file not found", {
        key,
        locale,
      });
      return;
    }

    const nextAudio = options.createAudio(source);
    const sequence = elementSequence;
    audio = nextAudio;
    nextAudio.currentTime = 0;
    nextAudio.volume = volume;
    nextAudio.addEventListener("ended", () => {
      if (sequence !== elementSequence || audio !== nextAudio) return;
      audio = null;
      status = "completed";
      publish();
      options.onCompleted?.(key);
    });
    nextAudio.addEventListener("error", () => {
      if (sequence !== elementSequence || audio !== nextAudio) return;
      audio = null;
      status = "error";
      publish();
      void reportOperationalError({
        action: "audio.playback-event",
        severity: "error",
        source: "native",
        transport: "html-audio",
        component: "audioPlaybackController",
        error: new Error("Audio element reported a playback error"),
        context: {
          audioKey: key,
        },
      });
    });

    status = "playing";
    publish();
    void nextAudio.play().catch((error: unknown) => {
      if (sequence !== elementSequence || audio !== nextAudio) return;
      status = "blocked";
      publish();
      frontdeskDiagnosticLogger.warn("Audio autoplay blocked", {
        key,
        error,
      });
      void reportOperationalError({
        action: "audio.play-rejected",
        severity: "warning",
        source: "native",
        transport: "html-audio",
        component: "audioPlaybackController",
        error,
        context: {
          audioKey: key,
        },
      });
    });
  };

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    register: (key, autoPlay) => {
      registrationSequence += 1;
      activeRegistration = registrationSequence;
      activeKey = key;
      stopElement();
      status = "idle";
      publish();
      if (autoPlay) playActive();
      return registrationSequence;
    },
    unregister: (registration) => {
      if (registration !== activeRegistration) return;
      stopElement();
      activeRegistration = null;
      activeKey = null;
      status = "idle";
      publish();
    },
    play: (registration) => {
      if (registration !== activeRegistration) return;
      playActive();
    },
    stop: (registration) => {
      if (registration !== activeRegistration) return;
      stopElement();
      status = activeKey === null ? "idle" : "completed";
      publish();
    },
    replay: playActive,
    clear: () => {
      stopElement();
      activeRegistration = null;
      activeKey = null;
      status = "idle";
      publish();
    },
    setLocale: (nextLocale) => {
      const normalized = normalizeLocale(nextLocale);
      if (normalized === locale) return;

      locale = normalized;
      stopElement();
      if (!isSupportedLanguage(locale)) {
        frontdeskDiagnosticLogger.warn("Audio locale is not supported", {
          locale: nextLocale,
        });
      }
      if (activeKey === null) {
        status = "idle";
        publish();
        return;
      }
      playActive();
    },
    setVolume: (nextVolume) => {
      volume = Math.max(0, Math.min(1, nextVolume / 100));
      if (audio !== null) audio.volume = volume;
    },
  };
}

const audioFilesByLocale: Partial<
  Record<FrontDeskLanguageCode, Partial<Record<AudioKey, string>>>
> = {};

for (const [path, source] of Object.entries(importedAudioFiles)) {
  const match = path.match(/\/([a-z]{2})\/\1-(.+)\.mp3$/);
  const locale = match?.[1];
  const key = match?.[2];
  if (!locale || !key || !isSupportedLanguage(locale) || !isAudioKey(key)) {
    frontdeskDiagnosticLogger.warn("Ignored unrecognized voiceover asset", {
      path,
    });
    continue;
  }
  const localeFiles = audioFilesByLocale[locale] ?? {};
  localeFiles[key] = source;
  audioFilesByLocale[locale] = localeFiles;
}

type AudioEvents = {
  "audio:load-play": AudioKey;
  "audio:play": void;
  "audio:pause": void;
  "audio:stop": void;
  "audio:completed": AudioKey;
  "audio:set-volume": number;
};

/**
 * Compatibility event surface for existing volume and host integrations.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * audioEmitter.emit("audio:set-volume", 50);
 * ```
 */
export const audioEmitter = mitt<AudioEvents>();

const controller = createAudioPlaybackController({
  assets: audioFilesByLocale,
  createAudio: (source) => new Audio(source),
  onCompleted: (key) => audioEmitter.emit("audio:completed", key),
});

/**
 * Registers the mounted step as the current spoken-guidance owner.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const registration = registerActiveGuidance("checkIn-cardCheck", true);
 * ```
 */
export function registerActiveGuidance(
  key: AudioKey,
  autoPlay: boolean,
): number {
  return controller.register(key, autoPlay);
}

/**
 * Clears guidance only when the matching mounted step still owns it.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * unregisterActiveGuidance(1);
 * ```
 */
export function unregisterActiveGuidance(registration: number): void {
  controller.unregister(registration);
}

/**
 * Plays guidance only for the matching mounted-step registration.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * playRegisteredGuidance(1);
 * ```
 */
export function playRegisteredGuidance(registration: number): void {
  controller.play(registration);
}

/**
 * Stops playback without forgetting the matching mounted guidance key.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * stopRegisteredGuidance(1);
 * ```
 */
export function stopRegisteredGuidance(registration: number): void {
  controller.stop(registration);
}

/**
 * Replays the active step's guidance in the selected audio locale.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * replayActiveGuidance();
 * ```
 */
export function replayActiveGuidance(): void {
  controller.replay();
}

/**
 * Sets the spoken-guidance locale and restarts the active key when it changes.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * setAudioLocale("en");
 * ```
 */
export function setAudioLocale(locale: string): void {
  controller.setLocale(locale);
}

/**
 * Returns the stable current spoken-guidance snapshot.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const snapshot = getAudioPlaybackSnapshot();
 * ```
 */
export function getAudioPlaybackSnapshot(): AudioPlaybackSnapshot {
  return controller.getSnapshot();
}

/**
 * Subscribes to spoken-guidance snapshot changes.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const unsubscribe = subscribeToAudioPlayback(() => undefined);
 * unsubscribe();
 * ```
 */
export function subscribeToAudioPlayback(listener: () => void): () => void {
  return controller.subscribe(listener);
}

let compatibilityRegistration: number | null = null;

audioEmitter.on("audio:load-play", (key) => {
  compatibilityRegistration = controller.register(key, true);
});
audioEmitter.on("audio:play", () => controller.replay());
audioEmitter.on("audio:pause", () => {
  if (compatibilityRegistration !== null) {
    controller.stop(compatibilityRegistration);
  }
});
audioEmitter.on("audio:stop", () => {
  controller.clear();
  compatibilityRegistration = null;
});
audioEmitter.on("audio:set-volume", (volume) => {
  controller.setVolume(volume);
});
